import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/common';
import api from '../services/api';
import { isHandoffDestination, isSafeRedirectUrl } from '../utils/redirectSafety';

export default function Handoff() {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const [status, setStatus] = useState('Preparing handoff…');

  useEffect(() => {
    const run = async () => {
      const nextUrl = searchParams.get('next') || searchParams.get('returnUrl');
      if (!nextUrl || !isSafeRedirectUrl(nextUrl)) {
        toast.error('Missing or invalid redirect URL.');
        setStatus('Failed');
        return;
      }

      // Wait for auth hydration to complete (AuthProvider fetches /auth/me on mount)
      if (loading) return;

      // If not logged in, send to the normal login page, preserving next.
      if (!user) {
        const loginUrl = new URL(`${window.location.origin}/login`);
        loginUrl.searchParams.set('next', nextUrl);
        window.location.replace(loginUrl.toString());
        return;
      }

      // Logged in: mint a handoff code and attach it to the callback URL.
      try {
        const url = new URL(nextUrl);
        // `handoff` names the AUDIENCE to mint for — 'admin' or 'dashboard'.
        // Absent means this is a plain redirect, not a token handoff.
        const audience = url.searchParams.get('handoff');
        if (!audience) {
          window.location.replace(nextUrl);
          return;
        }
        // THE CODE IS A BEARER CREDENTIAL, so it goes only to the frontend that
        // consumes it (isHandoffDestination) — `next` is a URL anyone can write.
        // The origin is sent along so the backend checks the same rule itself.
        if (!isHandoffDestination(audience, url.toString())) {
          toast.error('This sign-in cannot be sent there.');
          setStatus('Failed');
          return;
        }

        setStatus('Creating handoff code…');
        const resp = await api.createLoginHandoffCode(audience, null, url.origin);
        if (!resp?.code) throw new Error(`Failed to create ${audience} handoff code`);
        url.searchParams.set('code', resp.code);
        setStatus('Redirecting…');
        window.location.replace(url.toString());
      } catch (e) {
        toast.error(e.message || 'Handoff failed.');
        setStatus('Failed');
      }
    };

    run();
  }, [loading, user, searchParams]);

  return (
    <div style={{ padding: 24 }}>
      <h1>{status}</h1>
      {status === 'Failed' ? null : <p>Please wait…</p>}
    </div>
  );
}


